import { onAuthStateChanged, User } from "firebase/auth";
import { db, auth } from "@/lib/firebase";
import {
  createQuiz,
  updateQuiz,
  deleteQuiz,
  subscribeQuizzes,
  type Quiz as QuizDoc,
  type QuizSummary,
} from '@/data/quizzes';

  export type Quiz = {
    id: string;
    title: string;
    description?: string;
    createdAt: number;
    ownerId: string;
  };

type Listener = (quizzes: Quiz[]) => void;

class QuizStore {
  private static _instance: QuizStore | null = null;
  static get instance(): QuizStore {
    if (!this._instance) this._instance = new QuizStore();
    return this._instance!;
  }

  private db = db;
  private user: User | null = null;
  private unsub: (() => void) | null = null;
  private listeners = new Set<Listener>();
  private state: Quiz[] = [];

  /** Call once from each runtime (web app & Office pane) */
  async init(): Promise<void> {
    if (this.unsub) return;
    onAuthStateChanged(auth, (u) => {
      this.user = u;
      this.attachRealtime(); // bind listener when user available
    });
  }

  /** Subscribe to user's quizzes in real time */
  private attachRealtime() {
    // Clean up existing subscription first
    if (this.unsub) { 
      this.unsub(); 
      this.unsub = null; 
    }
    
    // If no user (logged out), clear state and return
    if (!this.user) {
      this.state = [];
      this.emit();
      return;
    }

    this.unsub = subscribeQuizzes({
      ownerId: this.user.uid,
      onUpdate: (docs: QuizSummary[]) => {
        const next: Quiz[] = docs.map(d => ({
          id: d.id,
          title: (d as any).title ?? "",
          description: (d as any).description ?? "",
          createdAt: (d.createdAt as any)?.toMillis
            ? (d.createdAt as any).toMillis()
            : (typeof (d as any).createdAt === "number" ? (d as any).createdAt : Date.now()),
          ownerId: (d as any).ownerId || "",
        }));
        this.state = next;
        this.emit();
      },
    });
  }

  /** Public API */

  getAll(): Quiz[] { return this.state; }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    // emit current immediately to keep lists fresh
    fn(this.state);
    return () => this.listeners.delete(fn);
  }

  private emit() { for (const l of this.listeners) l(this.state); }

    async create(input: { title: string; description?: string }): Promise<string> {
      const id = await createQuiz({
        title: input.title,
        description: input.description,
      });
      const newItem: Quiz = {
        id,
        title: input.title,
        description: input.description,
        createdAt: Date.now(),
        ownerId: this.user!.uid,
      };
      this.state = [...this.state, newItem];
      this.emit();
      return id;
    }

    async update(id: string, patch: Partial<Pick<Quiz, "title" | "description">>) {
      await updateQuiz(this.db, id, patch as any, this.user!);
    }

    async remove(id: string) {
      await deleteQuiz(this.db, id, this.user!);
    }
  }

export const quizStore = QuizStore.instance;

